import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Stack,
  Avatar,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  Badge,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ShoppingCart,
  Visibility,
  FilterList,
  Download,
  TrendingUp,
  Assessment,
  Schedule,
  CheckCircle,
  Pending,
  Error as ErrorIcon,
  Receipt,
  LocalShipping,
  Person,
  CalendarToday,
  LocationOn,
  Description,
  Close,
  Info,
  HighlightOff,
  Inventory2,
  ChevronLeft,
  ChevronRight,
  SwapHoriz,
} from '@mui/icons-material';
import { Alert, AlertTitle } from '@mui/material';
import StatCard from '../components/Common/StatCard';
import { orderService } from '../services/orders';
import { useAuth } from '../contexts/AuthContext';
import Loader from '../components/Common/Loader';
import ErrorMessage from '../components/Common/ErrorMessage';
import { getProductIcon } from '../utils/productIcons';
import HarvestProgressBar from '../components/Common/HarvestProgressBar';
import fieldsService from '../services/fields';
import { canSelectShippedOrCompletedStatus, getOrderHarvestYmd } from '../utils/orderHarvestGate';
import { getEstimatedDeliveryLeadDays, formatShippingLeadAfterHarvest } from '../utils/fieldEstimatedDelivery';
import {
  getDeliverableHarvest,
  getHarvestUrgency,
  urgencyChipSx,
} from '../utils/farmerOrderHarvestDisplay';
import { isPastSeasonOrder, pastSeasonOrderLabel } from '../utils/pastSeasonOrder';

const orderProductIconSrc = (order) =>
  getProductIcon(order.subcategory || order.crop_type || order.category);

/** Progress bar must use the order's locked harvest date, never the relisted field schedule. */
const orderHarvestProgressItem = (order) => {
  const ymd = order?.order_selected_harvest_date || order?.delivery_date || null;
  const label = order?.selected_harvest_label || '';
  return {
    ...order,
    lock_order_harvest: true,
    order_selected_harvest_date: ymd,
    selected_harvest_date: ymd,
    harvest_date: ymd,
    delivery_date: ymd,
    selected_harvests: ymd ? [{ date: ymd, label }] : [],
    harvest_dates: ymd ? [{ date: ymd, label }] : [],
  };
};

/**
 * Extract delivery address from order notes (map checkout: `Shipping: Delivery | Address: ...`
 * or `| Deliver to: ...`; also splits on `|` and scans segments).
 */
const parseDeliveryAddressFromNotes = (notes) => {
  if (notes == null || typeof notes !== 'string') return '';
  const trimmed = notes.trim();
  if (!trimmed) return '';
  if (trimmed.includes('|')) {
    const parts = trimmed.split('|').map((p) => p.trim());
    for (const p of parts) {
      const m = p.match(/^Address:\s*(.+)$/i) || p.match(/^Deliver to:\s*(.+)$/i);
      if (m?.[1]) return m[1].trim();
    }
  }
  const patterns = [
    /\|\s*Address:\s*(.+)$/i,
    /\|\s*Deliver to:\s*(.+)$/i,
    /Address:\s*(.+)$/i,
    /Deliver to:\s*(.+)$/i,
  ];
  for (const re of patterns) {
    const m = trimmed.match(re);
    if (m?.[1]) return m[1].trim();
  }
  return '';
};

const isDeliveryMode = (order) => {
  const m = String(order?.mode_of_shipping || '').toLowerCase();
  return m === 'delivery' || m.includes('delivery');
};

/** One line + optional label for the orders table */
const harvestDateDisplay = (order) => {
  const ymd = getOrderHarvestYmd(order);
  if (ymd) {
    const d = new Date(String(ymd).includes('T') ? ymd : `${String(ymd).slice(0, 10)}T12:00:00`);
    const dateText = !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : String(ymd).slice(0, 10);
    return { dateText, label: (order?.selected_harvest_label || '').trim() || null };
  }
  const onlyLabel = (order?.selected_harvest_label || '').trim();
  if (onlyLabel) {
    return { dateText: onlyLabel, label: null };
  }
  return { dateText: null, label: null };
};

const ORDER_STATUS_LABELS = {
  pending: 'Pending',
  active: 'Active',
  shipped: 'Shipped',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const normalizeOrderStatus = (status) => {
  const s = String(status || '').trim().toLowerCase();
  return ORDER_STATUS_LABELS[s] ? s : 'active';
};

/** Status dropdown — MenuItems must stay direct Select children (no Tooltip wrappers) or MUI shows a blank value. */
const FarmerOrderStatusSelect = ({
  order,
  updatingStatus,
  onChange,
  fullWidth = true,
  size = 'small',
}) => {
  const status = normalizeOrderStatus(order?.status);
  const harvestOk = canSelectShippedOrCompletedStatus(order);
  const ymd = getOrderHarvestYmd(order);
  const shipDisabled = !harvestOk && status !== 'shipped';
  const doneDisabled = !harvestOk && status !== 'completed';
  const lockHint = ymd
    ? `Available on or after harvest (${ymd})`
    : 'Set a harvest date on the order before Shipped or Completed.';
  const color =
    status === 'active' ? '#1d4ed8' :
    status === 'completed' ? '#059669' :
    status === 'shipped' ? '#0369a1' :
    status === 'pending' ? '#d97706' :
    '#ef4444';

  return (
    <Tooltip
      title={status === 'cancelled' ? 'Cancelled orders cannot be changed.' : ''}
      disableHoverListener={status !== 'cancelled'}
    >
      <FormControl size={size} fullWidth={fullWidth}>
        <Select
          value={status}
          displayEmpty
          renderValue={(selected) => ORDER_STATUS_LABELS[selected] || selected || '—'}
          onChange={(e) => {
            const next = normalizeOrderStatus(e.target.value);
            if (next === status) return;
            onChange(order, next);
          }}
          disabled={updatingStatus || status === 'cancelled'}
          sx={{
            borderRadius: 1.5,
            fontSize: '0.75rem',
            height: 32,
            backgroundColor: status === 'pending' ? '#fffbeb' : 'white',
            '& .MuiSelect-select': {
              py: 0.5,
              fontWeight: 600,
              color,
            },
          }}
        >
          <MenuItem value="active" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1d4ed8' }}>
            Active
          </MenuItem>
          <MenuItem
            value="shipped"
            disabled={shipDisabled}
            title={shipDisabled ? lockHint : undefined}
            sx={{ fontSize: '0.75rem', fontWeight: 600, color: shipDisabled ? '#94a3b8' : '#0369a1' }}
          >
            Shipped
          </MenuItem>
          <MenuItem
            value="completed"
            disabled={doneDisabled}
            title={doneDisabled ? lockHint : undefined}
            sx={{ fontSize: '0.75rem', fontWeight: 600, color: doneDisabled ? '#94a3b8' : '#059669' }}
          >
            Completed
          </MenuItem>
          <MenuItem value="cancelled" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#ef4444' }}>
            Cancelled
          </MenuItem>
        </Select>
      </FormControl>
    </Tooltip>
  );
};

const FarmOrders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const tableScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [rejectRefundOpen, setRejectRefundOpen] = useState(false);
  const [rejectRefundContext, setRejectRefundContext] = useState({ requestId: null, fieldName: '' });
  const [rejectFarmerNote, setRejectFarmerNote] = useState('');
  const [refundActionLoading, setRefundActionLoading] = useState(false);
  const [harvestCompleteOpen, setHarvestCompleteOpen] = useState(false);
  const [harvestOrder, setHarvestOrder] = useState(null);
  const [harvestAmount, setHarvestAmount] = useState('');
  const [harvestUnit, setHarvestUnit] = useState('kg');
  const [harvestNote, setHarvestNote] = useState('');
  const [harvestFormError, setHarvestFormError] = useState(null);

  const updateTableScrollHints = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  const scrollTableBy = useCallback((direction) => {
    const el = tableScrollRef.current;
    if (!el) return;
    const step = Math.max(240, Math.round(el.clientWidth * 0.5));
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (isMobile || loading) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return undefined;
    }

    let attachedEl = null;
    let ro = null;
    let cancelled = false;

    const detach = () => {
      if (attachedEl) {
        attachedEl.removeEventListener('scroll', updateTableScrollHints);
        attachedEl = null;
      }
      window.removeEventListener('resize', updateTableScrollHints);
      if (ro) {
        ro.disconnect();
        ro = null;
      }
    };

    const attach = () => {
      if (cancelled) return;
      const el = tableScrollRef.current;
      if (!el || el === attachedEl) {
        if (el) updateTableScrollHints();
        return;
      }
      detach();
      attachedEl = el;
      updateTableScrollHints();
      el.addEventListener('scroll', updateTableScrollHints, { passive: true });
      window.addEventListener('resize', updateTableScrollHints);
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(updateTableScrollHints);
        ro.observe(el);
      }
    };

    const raf = window.requestAnimationFrame(attach);
    const timeout = window.setTimeout(attach, 80);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
      window.clearTimeout(timeout);
      detach();
    };
  }, [isMobile, loading, orders.length, filter, updateTableScrollHints]);

  const loadOrders = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const [response, fieldsResponse] = await Promise.all([
        orderService.getFarmerOrdersWithFields(user.id),
        fieldsService.getAll(),
      ]);
      const apiOrders = Array.isArray(response.data) ? response.data : [];
      const ownFields = Array.isArray(fieldsResponse.data) ? fieldsResponse.data : [];
      const ownFieldById = new Map(
        ownFields.map((field) => [String(field.id), field])
      );

      const formattedOrders = apiOrders.map((order) => {
        const mergedField = ownFieldById.get(String(order.field_id)) || {};
        const rawLeadDays =
          order.estimated_delivery_days ??
          mergedField.estimated_delivery_days;
        const parsedLeadDays =
          rawLeadDays != null && rawLeadDays !== ''
            ? parseInt(String(rawLeadDays), 10)
            : NaN;
        const estimated_delivery_days =
          Number.isFinite(parsedLeadDays) && parsedLeadDays >= 1 ? parsedLeadDays : null;

        // Always lock to the order's own harvest selection — never live field harvest_dates
        // (list-again rewrites the field for a new season).
        const orderHarvestDate = order.selected_harvest_date || null;
        const orderHarvestLabel = order.selected_harvest_label || null;
        const fieldHarvestDates =
          order.field_harvest_dates ??
          mergedField.harvest_dates ??
          mergedField.harvestDates ??
          null;

        return {
        // Safe field metadata only (no harvest schedule / live production overwrite for past seasons)
        name: mergedField.name || order.field_name,
        image: mergedField.image || order.image_url,
        category: order.crop_type || mergedField.category,
        id: order.id,
        field_name: order.field_name || 'Unknown Field',
        buyer_name: order.buyer_name || 'Unknown Buyer',
        buyer_email: order.buyer_email || '',
        area_rented: `${order.quantity || 0} m²`,
        quantity: Number(order.quantity) || 0,
        total_cost: Number(order.total_price) || 0,
        status: normalizeOrderStatus(order.status),
        created_at: order.created_at,
        order_created_at: order.created_at,
        field_created_at:
          order.field_created_at ||
          order.fieldCreatedAt ||
          mergedField.created_at ||
          mergedField.createdAt ||
          null,
        crop_type: order.crop_type || 'Mixed',
        subcategory: order.subcategory || order.sub_category || mergedField.subcategory || null,
        price_per_unit: Number(order.price_per_m2) || 0,
        location: order.location || mergedField.location || 'Unknown',
        delivery_date: orderHarvestDate,
        estimated_delivery_days,
        order_selected_harvest_date: orderHarvestDate,
        selected_harvest_date: orderHarvestDate,
        selected_harvest_label: orderHarvestLabel,
        selected_harvests: orderHarvestDate
          ? [{ date: orderHarvestDate, label: orderHarvestLabel || '' }]
          : [],
        harvest_date: orderHarvestDate,
        harvest_dates: orderHarvestDate
          ? [{ date: orderHarvestDate, label: orderHarvestLabel || '' }]
          : [],
        field_harvest_dates: fieldHarvestDates,
        current_field_harvest_dates: fieldHarvestDates,
        mode_of_shipping: order.mode_of_shipping || 'delivery',
        field_id: order.field_id,
        farm_id: order.farm_id || mergedField.farm_id || mergedField.farmId || null,
        notes: order.notes ?? null,
        total_production:
          order.total_production ??
          mergedField.total_production ??
          mergedField.totalProduction ??
          null,
        total_production_unit:
          order.total_production_unit ||
          mergedField.total_production_unit ||
          mergedField.total_productionUnit ||
          'kg',
        total_area_m2:
          order.total_area_m2 ??
          mergedField.total_area_m2 ??
          mergedField.totalAreaM2 ??
          order.total_area ??
          mergedField.total_area ??
          null,
        operational_status:
          order.operational_status ||
          mergedField.operational_status ||
          'growing',
        last_season_yield:
          order.last_season_yield ??
          mergedField.last_season_yield ??
          null,
        field_harvest_total: order.field_harvest_total ?? null,
        harvest_unit: order.harvest_unit || null,
        harvest_declared_at: order.harvest_declared_at || null,
        harvest_allocated_qty: order.harvest_allocated_qty ?? null,
        harvest_estimated_qty: order.harvest_estimated_qty ?? null,
        delivery_address: parseDeliveryAddressFromNotes(order.notes),
        pending_refund_request_id: order.pending_refund_request_id || null,
        pending_refund_request_reason: order.pending_refund_request_reason || null,
        lock_order_harvest: true,
        is_past_season: false,
      };
      }).map((row) => ({
        ...row,
        is_past_season: isPastSeasonOrder(row),
      }));

      setOrders(formattedOrders);
    } catch (err) {
      console.error('Error loading farmer orders:', err);
      setError('Failed to load orders received. Please try again.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user) {
      loadOrders();
    } else {
      setLoading(false);
    }
  }, [user, loadOrders]);

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  const handleViewOnMap = (order) => {
    if (order.field_id) {
      // Navigate to farmer homepage with field_id parameter
      navigate(`/farmer?field_id=${order.field_id}`);
    }
  };

  const handleGoEnterHarvest = (order, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!order?.field_id) {
      navigate('/farmer/my-farms');
      return;
    }
    navigate(`/farmer/my-farms?field_id=${order.field_id}`);
  };

  const doStatusUpdate = useCallback(
    async (orderId, newStatus, extra = {}) => {
      if (!orderId || !newStatus) return false;
      setError(null);
      setUpdatingStatus(true);
      try {
        await orderService.updateOrderStatus(orderId, newStatus, extra);
        setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o)));
        if (selectedOrder?.id === orderId) {
          setSelectedOrder((prev) => (prev ? { ...prev, status: newStatus } : null));
        }
        return true;
      } catch (err) {
        console.error('Update order status error:', err);
        setError(err.response?.data?.error || err.message || 'Could not update order status');
        return false;
      } finally {
        setUpdatingStatus(false);
      }
    },
    [selectedOrder?.id]
  );

  const handleStatusChange = async (order, newStatus) => {
    if (!order?.id || !newStatus) return;
    const current = normalizeOrderStatus(order.status);
    const next = normalizeOrderStatus(newStatus);
    if (current === next) return;

    // Already completed after field harvest — don't open harvest dialog again.
    if (next === 'completed' && current !== 'completed' && String(user?.user_type || '') !== 'admin') {
      setHarvestOrder(order);
      setHarvestAmount('');
      setHarvestUnit(
        (order.total_production_unit && String(order.total_production_unit).trim()) || 'kg'
      );
      setHarvestNote('');
      setHarvestFormError(null);
      setHarvestCompleteOpen(true);
      return;
    }
    await doStatusUpdate(order.id, next, {});
  };

  const submitHarvestComplete = async () => {
    if (!harvestOrder?.id) return;
    const n = parseFloat(String(harvestAmount).replace(/,/g, ''));
    if (Number.isNaN(n) || n <= 0) {
      setHarvestFormError('Enter a positive number for the total amount you actually harvested (for this field).');
      return;
    }
    const unit = (harvestUnit || 'kg').trim() || 'kg';
    setHarvestFormError(null);
    const ok = await doStatusUpdate(harvestOrder.id, 'completed', {
      declared_harvest: {
        amount: n,
        unit,
        notes: harvestNote?.trim() || undefined,
      },
    });
    if (ok) {
      setHarvestCompleteOpen(false);
      setHarvestOrder(null);
    }
  };

  const handleApproveRefund = async (requestId) => {
    if (!requestId) return;
    if (
      !window.confirm(
        'Approve this refund? The order will be cancelled and coins returned to the buyer. If the order was already active or completed, the coin amount will be deducted from your wallet first.'
      )
    ) {
      return;
    }
    try {
      setError(null);
      setRefundActionLoading(true);
      await orderService.resolveRefundRequest(requestId, { action: 'approve' });
      await loadOrders();
    } catch (err) {
      console.error('Approve refund error:', err);
      setError(err.response?.data?.error || err.message || 'Could not approve refund');
    } finally {
      setRefundActionLoading(false);
    }
  };

  const openRejectRefund = (requestId, fieldName) => {
    setRejectRefundContext({ requestId, fieldName: fieldName || 'this field' });
    setRejectFarmerNote('');
    setRejectRefundOpen(true);
  };

  const submitRejectRefund = async () => {
    const { requestId } = rejectRefundContext;
    if (!requestId) return;
    try {
      setError(null);
      setRefundActionLoading(true);
      await orderService.resolveRefundRequest(requestId, {
        action: 'reject',
        farmer_response: rejectFarmerNote.trim() || undefined,
      });
      setRejectRefundOpen(false);
      await loadOrders();
    } catch (err) {
      console.error('Reject refund error:', err);
      setError(err.response?.data?.error || err.message || 'Could not decline refund request');
    } finally {
      setRefundActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'confirmed':
      case 'active':
        return 'primary';
      case 'shipped':
        return 'info';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleReportClick = () => {
    setReportOpen(true);
  };

  const handleCloseReport = () => {
    setReportOpen(false);
  };

  const handleDownloadReport = (format = 'pdf') => {
    const reportData = {
      generatedAt: new Date().toLocaleString(),
      totalOrders: filteredOrders.length,
      revenue: filteredOrders.reduce((sum, o) => sum + (Number(o.total_cost) || 0), 0),
      orders: filteredOrders.map(order => ({
        id: order.id,
        field: order.field_name,
        buyer: order.buyer_name,
        area: order.area_rented,
        cost: order.total_cost,
        status: order.status,
        date: new Date(order.order_created_at || order.created_at).toLocaleDateString(),
        deliverTo: order.delivery_address || '',
      }))
    };

    if (format === 'csv') {
      const headers = ['Order ID', 'Field Name', 'Buyer Name', 'Deliver to', 'Area', 'Total Cost', 'Status', 'Date'];
      const csvEscape = (cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`;
      const rows = reportData.orders.map(o => [
        o.id,
        o.field,
        o.buyer,
        o.deliverTo,
        o.area,
        `$${Number(o.cost).toFixed(2)}`,
        o.status,
        o.date
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map((row) => row.map(csvEscape).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `orders-report-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const printWindow = window.open('', '_blank');
      const reportHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Orders Report</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
              h1 { color: #1e293b; border-bottom: 2px solid #4caf50; padding-bottom: 10px; }
              h2 { color: #059669; margin-top: 30px; }
              .summary { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin: 20px 0; }
              .summary-card { background: #f8fafc; padding: 15px; border-radius: 8px; text-align: center; border: 1px solid #e2e8f0; }
              .summary-value { font-size: 24px; font-weight: bold; color: #1e293b; margin-bottom: 5px; }
              .summary-label { font-size: 12px; color: #64748b; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th { background-color: #4caf50; color: white; padding: 12px; text-align: left; font-weight: bold; }
              td { padding: 10px; border-bottom: 1px solid #e2e8f0; }
              tr:nth-child(even) { background-color: #f8fafc; }
              .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #64748b; text-align: center; }
              @media print { body { margin: 0; padding: 15px; } .no-print { display: none; } }
            </style>
          </head>
          <body>
            <h1>Orders Report</h1>
            <p><strong>Generated:</strong> ${reportData.generatedAt}</p>
            <div class="summary">
              <div class="summary-card">
                <div class="summary-value">${reportData.totalOrders}</div>
                <div class="summary-label">Total Orders</div>
              </div>
              <div class="summary-card">
                <div class="summary-value">$${reportData.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                <div class="summary-label">Total Revenue</div>
              </div>
            </div>
            <h2>Order Details</h2>
            <table>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Field</th>
                  <th>Buyer</th>
                  <th>Deliver to</th>
                  <th>Area</th>
                  <th>Cost</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                ${reportData.orders.map(order => `
                  <tr>
                    <td>#${order.id}</td>
                    <td>${order.field}</td>
                    <td>${order.buyer}</td>
                    <td>${order.deliverTo || '—'}</td>
                    <td>${order.area}</td>
                    <td>$${Number(order.cost).toFixed(2)}</td>
                    <td>${order.status}</td>
                    <td>${order.date}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            <div class="footer">
              <p>This report was generated on ${reportData.generatedAt}</p>
            </div>
            <script>
              window.onload = function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              };
            </script>
          </body>
        </html>
      `;

      printWindow.document.write(reportHTML);
      printWindow.document.close();
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (filter === 'all') return true;
    return order.status === filter;
  });

  if (loading) return <Loader message="Loading orders received..." />;
  if (error) return <ErrorMessage message={error} onRetry={loadOrders} />;

  const totalRevenue = orders.reduce((sum, o) => sum + (Number(o.total_cost) || 0), 0);
  const activeOrders = orders.filter((o) =>
    ['active', 'pending', 'shipped'].includes(o.status)
  ).length;
  const completedOrders = orders.filter((o) => o.status === 'completed').length;
  const completionRate =
    orders.length > 0 ? (completedOrders / orders.length) * 100 : 0;
  const pendingRefundCount = orders.filter((o) => o.pending_refund_request_id).length;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        p: 3,
      }}
    >
      <Box sx={{ maxWidth: '1400px', mx: 'auto', mb: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#1e293b',
                mb: 0.5,
                fontSize: '1.75rem',
              }}
            >
              Orders Received
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
              Orders placed by buyers on your fields — manage and update status
            </Typography>
          </Box>

        </Stack>

        {/* Payment Logic Info Alert */}
        <Alert
          severity="info"
          variant="outlined"
          icon={<Info fontSize="large" />}
          sx={{
            mb: 3,
            borderRadius: 2,
            backgroundColor: '#f0f9ff',
            borderColor: '#bae6fd',
            '& .MuiAlert-icon': { color: '#0284c7' },
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}
        >
          <AlertTitle sx={{ fontWeight: 700, color: '#0369a1' }}>Orders & field harvest</AlertTitle>
          New rentals are <strong>confirmed automatically</strong> — payment is credited when the buyer places the order. Use <strong>My Farms</strong> on each field to declare total harvest (split across renters) and mark the field as shipped.
          <Box sx={{ mt: 1.5, color: '#0f172a' }}>
            <strong>Per-order status:</strong> Shipped / Completed on or after the buyer&apos;s harvest date. Prefer declaring harvest on the field in My Farms so all renters get their share at once.
          </Box>
          {/* Legacy pending acceptance (disabled when AUTO_ACCEPT_ORDERS is on):
          <Box sx={{ mt: 1.5, color: '#0f172a' }}>
            Pending → Active to confirm and receive coins; 7-day auto-cancel if not accepted.
          </Box>
          */}
          <Box sx={{ mt: 1, color: '#b91c1c' }}>
            <strong>Warning:</strong> If you cancel an <em>Active</em> or <em>Completed</em> order, the coins will be automatically deducted from your wallet and refunded to the buyer.
          </Box>
        </Alert>

        {pendingRefundCount > 0 && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>Buyer refund requests</AlertTitle>
            {pendingRefundCount === 1
              ? 'One order has a pending refund request.'
              : `${pendingRefundCount} orders have pending refund requests.`}{' '}
            Use the green check to approve (cancel the order and refund coins) or the red icon to decline.
          </Alert>
        )}

        {/* Stats */}
        <div className="mb-3 grid max-w-[480px] grid-cols-2 gap-3 md:max-w-none md:grid-cols-4">
          <StatCard
            icon={<ShoppingCart sx={{ fontSize: 20 }} />}
            iconBg="#dbeafe"
            iconColor="#1d4ed8"
            value={orders.length}
            label="Total Orders"
          />
          <StatCard
            icon={<LocalShipping sx={{ fontSize: 20 }} />}
            iconBg="#dcfce7"
            iconColor="#059669"
            value={activeOrders}
            label="Active Orders"
          />
          <StatCard
            icon={<TrendingUp sx={{ fontSize: 20 }} />}
            iconBg="#fef3c7"
            iconColor="#d97706"
            value={`$${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            label="Total Revenue"
          />
          <StatCard
            icon={<Assessment sx={{ fontSize: 20 }} />}
            iconBg="#f3e8ff"
            iconColor="#7c3aed"
            value={`${completionRate.toFixed(0)}%`}
            label="Completion Rate"
          />
        </div>

        {/* Filters */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            backgroundColor: 'white',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
              Filter Orders
            </Typography>
            <Badge badgeContent={filteredOrders.length} color="primary">
              <FilterList color="action" sx={{ fontSize: 20 }} />
            </Badge>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {[
              { key: 'all', label: 'All Orders', icon: <ShoppingCart /> },
              { key: 'pending', label: 'Pending', icon: <Schedule /> },
              { key: 'active', label: 'Active', icon: <LocalShipping /> },
              { key: 'shipped', label: 'Shipped', icon: <Inventory2 /> },
              { key: 'completed', label: 'Completed', icon: <CheckCircle /> },
              { key: 'cancelled', label: 'Cancelled', icon: <ErrorIcon /> },
            ].map(({ key, label, icon }) => (
              <Button
                key={key}
                variant={filter === key ? 'contained' : 'outlined'}
                size="medium"
                onClick={() => setFilter(key)}
                startIcon={icon}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  ...(filter === key && {
                    backgroundColor: '#4caf50',

                    color: 'white',
                  }),
                }}
              >
                {label}
              </Button>
            ))}
          </Stack>
        </Paper>

        {/* Orders Table */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            backgroundColor: 'white',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              gap={1.5}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                  Order History
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  {filteredOrders.length} {filter === 'all' ? 'total' : filter} orders
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                {!isMobile && (canScrollLeft || canScrollRight) && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 999,
                      backgroundColor: '#eff6ff',
                      border: '1px solid #bfdbfe',
                    }}
                  >
                    <SwapHoriz sx={{ fontSize: 16, color: '#1d4ed8' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                      Scroll for more columns
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Scroll table left"
                      disabled={!canScrollLeft}
                      onClick={() => scrollTableBy(-1)}
                      sx={{
                        width: 28,
                        height: 28,
                        color: '#1d4ed8',
                        backgroundColor: '#fff',
                        border: '1px solid #bfdbfe',
                        '&:hover': { backgroundColor: '#dbeafe' },
                        '&.Mui-disabled': { opacity: 0.35 },
                      }}
                    >
                      <ChevronLeft sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Scroll table right"
                      disabled={!canScrollRight}
                      onClick={() => scrollTableBy(1)}
                      sx={{
                        width: 28,
                        height: 28,
                        color: '#1d4ed8',
                        backgroundColor: '#fff',
                        border: '1px solid #bfdbfe',
                        '&:hover': { backgroundColor: '#dbeafe' },
                        '&.Mui-disabled': { opacity: 0.35 },
                      }}
                    >
                      <ChevronRight sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Stack>
                )}
                <Button
                  variant="outlined"
                  onClick={handleReportClick}
                  startIcon={<Download sx={{ fontSize: 18 }} />}
                  sx={{ borderRadius: 2, px: 2, py: 1, fontSize: '0.8rem' }}
                >
                  Export
                </Button>
              </Stack>
            </Stack>
          </Box>

          {filteredOrders.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <ShoppingCart sx={{ fontSize: 48, color: '#cbd5e1', mb: 1.5 }} />
              <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontSize: '1rem' }}>
                {filter === 'all' ? 'No orders received yet' : `No ${filter} orders found`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                {filter === 'all' ? 'Orders from buyers will appear here.' : 'Try a different filter.'}
              </Typography>
            </Box>
          ) : isMobile ? (
            <Stack spacing={1.5} sx={{ p: 1.5 }}>
              {filteredOrders.map((order) => {
                const urgency = getHarvestUrgency(order);
                const deliverable = getDeliverableHarvest(order, orders);
                const { dateText, label: harvestLabel } = harvestDateDisplay(order);
                return (
                  <Paper
                    key={order.id}
                    elevation={0}
                    sx={{
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      p: 1.75,
                      backgroundColor: '#fff',
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 1.25 }}>
                      <Box
                        component="img"
                        src={orderProductIconSrc(order)}
                        alt={order.field_name}
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 1.5,
                          objectFit: 'cover',
                          border: '1px solid #e2e8f0',
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                color: '#0f172a',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {order.field_name}
                            </Typography>
                            {order.is_past_season ? (
                              <Chip
                                size="small"
                                label={pastSeasonOrderLabel(order)}
                                sx={{
                                  mt: 0.5,
                                  height: 20,
                                  bgcolor: '#f5f3ff',
                                  color: '#6d28d9',
                                  border: '1px solid #ddd6fe',
                                  fontWeight: 700,
                                  '& .MuiChip-label': { fontSize: '0.62rem', px: 0.75 },
                                }}
                              />
                            ) : null}
                          </Box>
                          <Chip
                            size="small"
                            label={urgency.shortLabel || urgency.label}
                            sx={{
                              ...urgencyChipSx(urgency.tone),
                              flexShrink: 0,
                              height: 22,
                              '& .MuiChip-label': { fontSize: '0.68rem', px: 0.75, whiteSpace: 'nowrap' },
                            }}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {order.buyer_name}
                          {order.buyer_email ? ` · ${order.buyer_email}` : ''}
                        </Typography>
                      </Box>
                    </Stack>

                    <Box sx={{ mb: 1.25, minWidth: 0, overflow: 'hidden' }}>
                      {order.is_past_season ? (
                        <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 600 }}>
                          Past season order — harvest date locked to this rental
                        </Typography>
                      ) : (
                        <HarvestProgressBar item={orderHarvestProgressItem(order)} compact showDate={false} daysShort label="" />
                      )}
                    </Box>

                    <Stack spacing={1} sx={{ mb: 1.25 }}>
                      <Stack direction="row" justifyContent="space-between" gap={1}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, flexShrink: 0 }}>
                          Harvest
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right', color: '#334155' }}>
                          {dateText || '—'}
                          {harvestLabel ? ` · ${harvestLabel}` : ''}
                        </Typography>
                      </Stack>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
                          Deliver to
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.8rem',
                            color: order.delivery_address ? '#0f172a' : '#94a3b8',
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            lineHeight: 1.4,
                          }}
                        >
                          {order.delivery_address || '—'}
                        </Typography>
                      </Box>
                      <Stack direction="row" justifyContent="space-between" gap={2} flexWrap="wrap">
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                            Area
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            {order.area_rented}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                            Revenue
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#059669' }}>
                            ${Number(order.total_cost).toFixed(2)}
                          </Typography>
                        </Box>
                      </Stack>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
                          Est vs Actual
                        </Typography>
                        {deliverable.declared && deliverable.actualText ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#059669' }}>
                              Actual {deliverable.actualText}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Est {deliverable.estimatedText || '—'}
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            {deliverable.estimatedText && (
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 700, mb: 0.25 }}>
                                Est {deliverable.estimatedText}
                              </Typography>
                            )}
                            <Button
                              size="small"
                              onClick={(e) => handleGoEnterHarvest(order, e)}
                              sx={{
                                p: 0,
                                minWidth: 0,
                                textTransform: 'none',
                                fontSize: '0.78rem',
                                fontWeight: 700,
                                color: '#b45309',
                                justifyContent: 'flex-start',
                                '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                              }}
                            >
                              Enter harvest →
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 1.25 }} />

                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                      <Box sx={{ minWidth: 120, flex: 1 }}>
                        <FarmerOrderStatusSelect
                          order={order}
                          updatingStatus={updatingStatus}
                          onChange={handleStatusChange}
                        />
                      </Box>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {order.field_id && (
                          <Button
                            size="small"
                            startIcon={<LocationOn sx={{ fontSize: 16 }} />}
                            onClick={() => handleViewOnMap(order)}
                            sx={{
                              textTransform: 'none',
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              color: '#1d4ed8',
                              px: 0.75,
                              minWidth: 0,
                              '&:hover': { backgroundColor: '#eff6ff' },
                            }}
                          >
                            View on map
                          </Button>
                        )}
                        <Button
                          size="small"
                          startIcon={<Visibility sx={{ fontSize: 16 }} />}
                          onClick={() => handleViewDetails(order)}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            color: '#059669',
                            px: 0.75,
                            minWidth: 0,
                            '&:hover': { backgroundColor: '#ecfdf5' },
                          }}
                        >
                          View details
                        </Button>
                        {order.pending_refund_request_id && (
                          <>
                            <IconButton
                              size="small"
                              disabled={refundActionLoading || updatingStatus}
                              onClick={() => handleApproveRefund(order.pending_refund_request_id)}
                              sx={{ color: '#059669' }}
                            >
                              <CheckCircle sx={{ fontSize: 18 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              disabled={refundActionLoading || updatingStatus}
                              onClick={() => openRejectRefund(order.pending_refund_request_id, order.field_name)}
                              sx={{ color: '#dc2626' }}
                            >
                              <HighlightOff sx={{ fontSize: 18 }} />
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Box sx={{ position: 'relative' }}>
              {canScrollLeft && (
                <Box
                  sx={{
                    pointerEvents: 'none',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 28,
                    zIndex: 3,
                    background: 'linear-gradient(to right, rgba(248,250,252,0.95), transparent)',
                  }}
                />
              )}
              {canScrollRight && (
                <Box
                  sx={{
                    pointerEvents: 'none',
                    position: 'absolute',
                    right: 220,
                    top: 0,
                    bottom: 0,
                    width: 28,
                    zIndex: 3,
                    background: 'linear-gradient(to left, rgba(248,250,252,0.95), transparent)',
                  }}
                />
              )}
            <TableContainer
              ref={tableScrollRef}
              sx={{
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { height: 8 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: 4 },
              }}
            >
              <Table
                sx={{
                  minWidth: 1320,
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': {
                    verticalAlign: 'top',
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 1.5, width: 170 }}>
                      Field / Product
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 1.5, width: 140 }}>
                      Buyer
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 1.5, width: 120 }}>
                      Harvest / ordered
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 1.5, width: 110 }}>
                      Urgency
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 1.5, width: 260 }}>
                      Deliver to
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 1.5, width: 80 }}>
                      Area
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 1.5, width: 170 }}>
                      Est vs Actual
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.8rem', py: 1.5, width: 90 }}>
                      Revenue
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: '#475569',
                        fontSize: '0.8rem',
                        py: 1.5,
                        width: 220,
                        position: 'sticky',
                        right: 0,
                        zIndex: 4,
                        backgroundColor: '#f8fafc',
                        boxShadow: '-10px 0 16px -12px rgba(15, 23, 42, 0.28)',
                        borderLeft: '1px solid #e2e8f0',
                      }}
                    >
                      Status / actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders.map((order, index) => (
                    <TableRow
                      key={order.id}
                      sx={{
                        '&:hover': { backgroundColor: '#f8fafc' },
                        borderBottom: index === filteredOrders.length - 1 ? 'none' : '1px solid #e2e8f0',
                      }}
                    >
                      <TableCell sx={{ py: 1.5, pr: 1 }}>
                        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ minWidth: 0 }}>
                          <Box
                            component="img"
                            src={orderProductIconSrc(order)}
                            alt={order.field_name}
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 1.5,
                              objectFit: 'cover',
                              border: '1px solid #e2e8f0',
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                mb: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={order.field_name}
                            >
                              {order.field_name}
                            </Typography>
                            {order.is_past_season ? (
                              <Chip
                                size="small"
                                label={pastSeasonOrderLabel(order)}
                                sx={{
                                  mb: 0.5,
                                  height: 18,
                                  bgcolor: '#f5f3ff',
                                  color: '#6d28d9',
                                  border: '1px solid #ddd6fe',
                                  fontWeight: 700,
                                  '& .MuiChip-label': { fontSize: '0.58rem', px: 0.6 },
                                }}
                              />
                            ) : null}
                            <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                              {order.is_past_season ? (
                                <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 600, fontSize: '0.65rem' }}>
                                  Season closed
                                </Typography>
                              ) : (
                                <HarvestProgressBar item={orderHarvestProgressItem(order)} compact showDate={false} daysShort label="" />
                              )}
                            </Box>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            wordBreak: 'break-word',
                          }}
                        >
                          {order.buyer_name}
                        </Typography>
                        {order.buyer_email && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: '0.7rem',
                              display: 'block',
                              wordBreak: 'break-all',
                              lineHeight: 1.3,
                            }}
                          >
                            {order.buyer_email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {(() => {
                          const { dateText, label } = harvestDateDisplay(order);
                          const orderedAt = new Date(order.order_created_at || order.created_at);
                          return (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                {dateText || '—'}
                              </Typography>
                              {label ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontSize: '0.68rem',
                                    display: 'block',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'normal',
                                  }}
                                >
                                  {label}
                                </Typography>
                              ) : null}
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block' }}>
                                Ordered {Number.isNaN(orderedAt.getTime()) ? '—' : orderedAt.toLocaleDateString()}
                              </Typography>
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }} onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const urgency = getHarvestUrgency(order);
                          return (
                            <Tooltip title={urgency.label}>
                              <Chip
                                size="small"
                                label={urgency.shortLabel || urgency.label}
                                sx={{
                                  ...urgencyChipSx(urgency.tone),
                                  height: 'auto',
                                  maxWidth: '100%',
                                  '& .MuiChip-label': {
                                    fontSize: '0.72rem',
                                    px: 0.85,
                                    py: 0.4,
                                    whiteSpace: 'normal',
                                    lineHeight: 1.2,
                                    overflow: 'visible',
                                    textOverflow: 'clip',
                                  },
                                }}
                              />
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {order.delivery_address ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.78rem',
                              lineHeight: 1.4,
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              color: '#0f172a',
                            }}
                          >
                            {order.delivery_address}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {order.area_rented}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }} onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const deliverable = getDeliverableHarvest(order);
                          if (deliverable.declared && deliverable.actualText) {
                            return (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.8rem', color: '#059669' }}>
                                  Actual {deliverable.actualText}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                  Est {deliverable.estimatedText || '—'}
                                </Typography>
                              </Box>
                            );
                          }
                          return (
                            <Box>
                              {deliverable.estimatedText && (
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#475569', mb: 0.25 }}>
                                  Est {deliverable.estimatedText}
                                </Typography>
                              )}
                              <Button
                                size="small"
                                variant="text"
                                onClick={(e) => handleGoEnterHarvest(order, e)}
                                sx={{
                                  p: 0,
                                  minWidth: 0,
                                  textTransform: 'none',
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  color: '#b45309',
                                  lineHeight: 1.3,
                                  justifyContent: 'flex-start',
                                  textAlign: 'left',
                                  whiteSpace: 'normal',
                                  '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                                }}
                              >
                                Enter harvest →
                              </Button>
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          ${Number(order.total_cost).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1,
                          position: 'sticky',
                          right: 0,
                          zIndex: 3,
                          backgroundColor: '#fff',
                          boxShadow: '-10px 0 16px -12px rgba(15, 23, 42, 0.28)',
                          borderLeft: '1px solid #e2e8f0',
                        }}
                      >
                        <Stack spacing={0.5}>
                          <FarmerOrderStatusSelect
                            order={order}
                            updatingStatus={updatingStatus}
                            onChange={handleStatusChange}
                          />
                          <Stack direction="row" alignItems="center" spacing={0.25} flexWrap="nowrap">
                            {order.field_id && (
                              <Button
                                size="small"
                                startIcon={<LocationOn sx={{ fontSize: '0.85rem !important' }} />}
                                onClick={() => handleViewOnMap(order)}
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  color: '#1d4ed8',
                                  px: 0.4,
                                  py: 0,
                                  minWidth: 0,
                                  minHeight: 0,
                                  lineHeight: 1.2,
                                  whiteSpace: 'nowrap',
                                  '& .MuiButton-startIcon': { mr: 0.35 },
                                  '&:hover': { backgroundColor: '#eff6ff' },
                                }}
                              >
                                View on map
                              </Button>
                            )}
                            <Button
                              size="small"
                              startIcon={<Visibility sx={{ fontSize: '0.85rem !important' }} />}
                              onClick={() => handleViewDetails(order)}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                color: '#059669',
                                px: 0.4,
                                py: 0,
                                minWidth: 0,
                                minHeight: 0,
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap',
                                '& .MuiButton-startIcon': { mr: 0.35 },
                                '&:hover': { backgroundColor: '#ecfdf5' },
                              }}
                            >
                              View details
                            </Button>
                            {order.pending_refund_request_id && (
                              <>
                                <Tooltip
                                  title={
                                    order.pending_refund_request_reason
                                      ? `Buyer message: ${order.pending_refund_request_reason}\n\nApprove: cancel order and refund coins.`
                                      : 'Approve refund (cancel order, return coins to buyer)'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={refundActionLoading || updatingStatus}
                                      onClick={() => handleApproveRefund(order.pending_refund_request_id)}
                                      sx={{ color: '#059669', p: 0.25 }}
                                    >
                                      <CheckCircle sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Decline refund request">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={refundActionLoading || updatingStatus}
                                      onClick={() => openRejectRefund(order.pending_refund_request_id, order.field_name)}
                                      sx={{ color: '#dc2626', p: 0.25 }}
                                    >
                                      <HighlightOff sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </>
                            )}
                          </Stack>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Order details dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ backgroundColor: '#dbeafe', color: '#1d4ed8' }}>
              <Receipt />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Order Details
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                  #{selectedOrder?.id}
                </Typography>
                {selectedOrder?.is_past_season ? (
                  <Chip
                    size="small"
                    label={pastSeasonOrderLabel(selectedOrder)}
                    sx={{
                      height: 22,
                      bgcolor: '#f5f3ff',
                      color: '#6d28d9',
                      border: '1px solid #ddd6fe',
                      fontWeight: 700,
                      '& .MuiChip-label': { fontSize: '0.68rem', px: 0.75 },
                    }}
                  />
                ) : null}
              </Stack>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedOrder && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '2px solid #bfdbfe',
                    background: 'linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)',
                  }}
                >
                  <Typography variant="overline" sx={{ fontWeight: 700, color: '#1d4ed8', letterSpacing: 0.08 }}>
                    What you are fulfilling
                  </Typography>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{ mt: 1.5 }}
                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                    flexWrap="wrap"
                  >
                    <Box sx={{ minWidth: 140 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                        Area purchased
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                        {selectedOrder.quantity != null && selectedOrder.quantity !== ''
                          ? `${Number(selectedOrder.quantity).toLocaleString(undefined, { maximumFractionDigits: 2 })} m²`
                          : selectedOrder.area_rented}
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                    <Box sx={{ minWidth: 120 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                        Order total
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#059669', lineHeight: 1.2 }}>
                        ${Number(selectedOrder.total_cost).toFixed(2)}
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                    <Box sx={{ minWidth: 180 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                        Est vs Actual
                      </Typography>
                      {(() => {
                        const deliverable = getDeliverableHarvest(selectedOrder);
                        if (deliverable.declared && deliverable.actualText) {
                          return (
                            <>
                              <Typography variant="h5" sx={{ fontWeight: 800, color: '#059669', lineHeight: 1.2 }}>
                                {deliverable.actualText}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                                Actual · Est {deliverable.estimatedText || '—'}
                              </Typography>
                            </>
                          );
                        }
                        return (
                          <>
                            <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.25 }}>
                              Est {deliverable.estimatedText || '—'}
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => handleGoEnterHarvest(selectedOrder)}
                              sx={{
                                mt: 0.5,
                                px: 0,
                                minWidth: 0,
                                textTransform: 'none',
                                fontWeight: 700,
                                color: '#b45309',
                                '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                              }}
                            >
                              Enter harvest →
                            </Button>
                          </>
                        );
                      })()}
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                        Shipping
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
                        <LocalShipping sx={{ fontSize: 22, color: '#1d4ed8' }} />
                        <Typography variant="body1" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                          {String(selectedOrder.mode_of_shipping || '—').replace(/_/g, ' ')}
                        </Typography>
                      </Stack>
                      {isDeliveryMode(selectedOrder) ? (
                        selectedOrder.delivery_address ? (
                          <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mt: 1 }}>
                            <LocationOn sx={{ fontSize: 22, color: '#1d4ed8', mt: 0.25 }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Deliver to
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                                {selectedOrder.delivery_address}
                              </Typography>
                            </Box>
                          </Stack>
                        ) : selectedOrder.notes ? (
                          <Alert severity="info" icon={<LocationOn />} sx={{ mt: 1.5, borderRadius: 2 }}>
                            <AlertTitle sx={{ fontWeight: 700 }}>Delivery details</AlertTitle>
                            <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {String(selectedOrder.notes).trim()}
                            </Typography>
                          </Alert>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            No delivery address was saved on this order. Contact the buyer for the ship-to address.
                          </Typography>
                        )
                      ) : (
                        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mt: 1 }}>
                          <LocationOn sx={{ fontSize: 22, color: '#64748b', mt: 0.25 }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Pickup location (field)
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                              {selectedOrder.location || '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                  {(selectedOrder.selected_harvest_label || selectedOrder.delivery_date) && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #cbd5e1' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                        Harvest window
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                        {[
                          selectedOrder.selected_harvest_label,
                          selectedOrder.delivery_date
                            ? new Date(selectedOrder.delivery_date).toLocaleDateString()
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                      {formatShippingLeadAfterHarvest(getEstimatedDeliveryLeadDays(selectedOrder, 2)) && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 600 }}>
                          Est. delivery: {formatShippingLeadAfterHarvest(getEstimatedDeliveryLeadDays(selectedOrder, 2))}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {(() => {
                    const deliverable = getDeliverableHarvest(selectedOrder);
                    const urgency = getHarvestUrgency(selectedOrder);
                    const areaLabel =
                      deliverable.orderArea != null && deliverable.fieldArea != null
                        ? `${Number(deliverable.orderArea).toLocaleString(undefined, { maximumFractionDigits: 2 })} m² of ${Number(deliverable.fieldArea).toLocaleString(undefined, { maximumFractionDigits: 2 })} m² field`
                        : null;
                    return (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #cbd5e1' }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                              Estimated vs Actual
                            </Typography>
                            {areaLabel && (
                              <Typography variant="caption" color="text.secondary">
                                For this order&apos;s area ({areaLabel})
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            size="small"
                            label={urgency.shortLabel || urgency.label}
                            sx={{
                              ...urgencyChipSx(urgency.tone),
                              height: 'auto',
                              '& .MuiChip-label': { fontSize: '0.7rem', py: 0.35 },
                            }}
                          />
                        </Stack>

                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={2}
                          divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid #e2e8f0',
                            backgroundColor: '#fff',
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                              Estimated
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: '#475569', lineHeight: 1.2 }}>
                              {deliverable.estimatedText || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              From field setup amount
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                              Actual yield
                            </Typography>
                            {deliverable.declared && deliverable.actualText ? (
                              <>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#059669', lineHeight: 1.2 }}>
                                  {deliverable.actualText}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                  Deliver this much to the buyer
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Typography variant="body1" sx={{ fontWeight: 700, color: '#b45309', mt: 0.5 }}>
                                  Not entered yet
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={() => handleGoEnterHarvest(selectedOrder)}
                                  sx={{
                                    mt: 0.5,
                                    px: 0,
                                    minWidth: 0,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    color: '#b45309',
                                    '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                                  }}
                                >
                                  Enter harvest on My Farms →
                                </Button>
                              </>
                            )}
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })()}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    backgroundColor: '#f8fafc',
                    height: '100%',
                    minHeight: 320,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Box
                      component="img"
                      src={orderProductIconSrc(selectedOrder)}
                      alt={selectedOrder.field_name}
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        objectFit: 'cover',
                        border: '1px solid #e2e8f0',
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      Field / Product
                    </Typography>
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1.5} sx={{ flex: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Field Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedOrder.field_name}
                      </Typography>
                      {selectedOrder.is_past_season ? (
                        <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 600, display: 'block', mt: 0.5 }}>
                          This rental is from a previous season. New sales on the relisted field are separate orders.
                        </Typography>
                      ) : null}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Crop / Category
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedOrder.crop_type}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Location
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedOrder.location}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Area
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedOrder.area_rented}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Price per m²
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#059669' }}>
                        ${Number(selectedOrder.price_per_unit).toFixed(2)}
                      </Typography>
                    </Box>
                    <Box>
                      {selectedOrder.is_past_season ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          Past season — harvest date stays on this order ({harvestDateDisplay(selectedOrder).dateText || 'locked'}).
                          Relisted field dates do not apply here.
                        </Alert>
                      ) : (
                        <HarvestProgressBar item={orderHarvestProgressItem(selectedOrder)} />
                      )}
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    backgroundColor: '#f8fafc',
                    height: '100%',
                    minHeight: 320,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Avatar sx={{ backgroundColor: '#fef3c7', color: '#d97706', width: 40, height: 40 }}>
                      <Person />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      Buyer & Order
                    </Typography>
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1.5} sx={{ flex: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Buyer Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedOrder.buyer_name}
                      </Typography>
                    </Box>
                    {selectedOrder.buyer_email && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                          Buyer Email
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedOrder.buyer_email}
                        </Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Shipping
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                        <LocalShipping sx={{ fontSize: 18, color: '#64748b' }} />
                        <Typography variant="body1" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                          {String(selectedOrder.mode_of_shipping || '—').replace(/_/g, ' ')}
                        </Typography>
                      </Stack>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedOrder.status}
                          color={getStatusColor(selectedOrder.status)}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            borderRadius: 2,
                            ...(selectedOrder.status === 'completed' && {
                              color: '#ffffff'
                            })
                          }}
                        />
                        <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic', color: '#64748b' }}>
                          Status can be updated directly from the orders table.
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Total Revenue
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#059669' }}>
                        ${Number(selectedOrder.total_cost).toFixed(2)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Order Date
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {new Date(selectedOrder.order_created_at || selectedOrder.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    {selectedOrder.delivery_date && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                          Harvest date (order)
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {new Date(selectedOrder.delivery_date).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                    {formatShippingLeadAfterHarvest(getEstimatedDeliveryLeadDays(selectedOrder, 2)) && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                          Est. delivery lead
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {formatShippingLeadAfterHarvest(getEstimatedDeliveryLeadDays(selectedOrder, 2))}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
          <Button onClick={() => setDetailsOpen(false)} sx={{ borderRadius: 2 }}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            sx={{
              backgroundColor: '#059669',

              borderRadius: 2,
              px: 3,
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Dialog */}
      <Dialog
        open={reportOpen}
        onClose={handleCloseReport}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                Orders Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overview of {filter === 'all' ? 'all' : filter} orders
              </Typography>
            </Box>
            <IconButton onClick={handleCloseReport} sx={{ color: '#64748b' }}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box>
            {/* Summary Statistics */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                    {filteredOrders.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Orders
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, backgroundColor: '#f0fdf4', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#059669', mb: 0.5 }}>
                    ${filteredOrders.reduce((sum, o) => sum + (Number(o.total_cost) || 0), 0).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Orders Summary Table */}
            <Paper sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1e293b' }}>
                Orders List
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Order ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Buyer</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Area</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Cost</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>#{order.id}</TableCell>
                        <TableCell>{order.field_name}</TableCell>
                        <TableCell>{order.buyer_name}</TableCell>
                        <TableCell>{order.area_rented}</TableCell>
                        <TableCell>${Number(order.total_cost).toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip
                            label={order.status}
                            color={getStatusColor(order.status)}
                            size="small"
                            sx={{ fontWeight: 600, height: 24 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Report generated on {new Date().toLocaleString()}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
          <Button
            onClick={() => handleDownloadReport('csv')}
            variant="outlined"
            startIcon={<Download />}
            sx={{ borderRadius: 1.5 }}
          >
            Download CSV
          </Button>
          <Button
            onClick={() => handleDownloadReport('pdf')}
            variant="outlined"
            startIcon={<Description />}
            sx={{ borderRadius: 1.5 }}
          >
            Download PDF
          </Button>
          <Button onClick={handleCloseReport} variant="contained" sx={{ borderRadius: 1.5, bgcolor: '#4caf50', color: '#ffffff', '&:hover': { bgcolor: '#059669' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectRefundOpen} onClose={() => !refundActionLoading && setRejectRefundOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Decline refund request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The buyer keeps the current order status and their coins are not refunded. Optionally add a short note (shown to
            the buyer).
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {rejectRefundContext.fieldName}
          </Typography>
          <TextField
            label="Note to buyer (optional)"
            fullWidth
            multiline
            minRows={2}
            value={rejectFarmerNote}
            onChange={(e) => setRejectFarmerNote(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRejectRefundOpen(false)} disabled={refundActionLoading}>
            Back
          </Button>
          <Button color="error" variant="contained" onClick={submitRejectRefund} disabled={refundActionLoading}>
            {refundActionLoading ? 'Saving…' : 'Decline request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={harvestCompleteOpen}
        onClose={() => {
          if (!updatingStatus) {
            setHarvestCompleteOpen(false);
            setHarvestOrder(null);
            setHarvestFormError(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>How much did you harvest?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            For <strong>{harvestOrder?.field_name || 'this field'}</strong>, enter the <strong>actual total</strong> you
            harvested this season. This is stored as your harvest record for the field (more in a good year, less in a
            bad year).
          </Typography>
          {harvestFormError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHarvestFormError(null)}>
              {harvestFormError}
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Amount harvested"
              value={harvestAmount}
              onChange={(e) => setHarvestAmount(e.target.value)}
              type="text"
              inputMode="decimal"
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Unit"
              value={harvestUnit}
              onChange={(e) => setHarvestUnit(e.target.value)}
              select
              fullWidth
            >
              {['kg', 'lb', 'g', 't', 'units', 'L', 'bushels'].map((u) => (
                <MenuItem key={u} value={u}>
                  {u}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            label="Note (optional)"
            value={harvestNote}
            onChange={(e) => setHarvestNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            placeholder="E.g. variety, weather, or how the crop was shared between buyers"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              if (!updatingStatus) {
                setHarvestCompleteOpen(false);
                setHarvestOrder(null);
                setHarvestFormError(null);
              }
            }}
            disabled={updatingStatus}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={submitHarvestComplete} disabled={updatingStatus} sx={{ bgcolor: '#059669' }}>
            {updatingStatus ? 'Saving…' : 'Save & mark completed'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FarmOrders;
